import React, { useContext, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { Loading3QuartersOutlined } from '@ant-design/icons'

import { useService } from '../../../hooks/use-service'
import { DomainIntegrationService } from '../../../services'
import { simpleNotification } from '../../../_utils/utils'
import { IntegrationProps } from '../interfaces'
import { handleResponseErrorMessage } from '../../../_utils/response-error-utils'
import { GetShopifyDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { DomainIntegrationsManagerContext } from '../context'
import { DomainIntegrationsManagerDispatchContext } from '../reducer'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { PlatformIntegrationCard } from '../../platform-integrations/platform-integration-card/platform-integration-card'
import { PlatformIntegrationModal } from '../../platform-integrations/platform-integration-modal/platform-integration-modal'
import { Descriptions, Input, Result } from 'antd'
import { ShopifyOAuthDialogState } from '../interfaces'

function getShopifyDomainName(domain: string) {
    return domain.replace(/\.myshopify\.com$/, '')
}

const ShopifyIntegration = (props: IntegrationProps<GetShopifyDomainIntegrationResponse>) => {
    const { loading, domain } = useContext(DomainIntegrationsManagerContext)
    const dispatch = useContext(DomainIntegrationsManagerDispatchContext)

    const mode = props.mode
    const integration = mode === 'edit' ? props.integration : null

    const [primaryActionLoading, setPrimaryActionLoading] = useState(false)
    const [oauthDialog, setOauthDialog] = useState<ShopifyOAuthDialogState>({ status: 'hidden' })
    const [setupDialogOpen, setSetupDialogOpen] = useState(false)
    const [shopName, setShopName] = useState('')
    const [connectLoading, setConnectLoading] = useState(false)
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
    const [removeActionLoading, setRemoveActionLoading] = useState(false)

    const svc = useService<DomainIntegrationService>(DomainIntegrationService)
    const history = useHistory()
    const { search: queryStringValue } = useLocation()

    const qs = queryString.parse(queryStringValue)

    const shopifyLogo = <span className="shopify-logo-color" />

    const closeOauthDialog = () => {
        history.replace({ hash: 'integrations', search: '' })
        setOauthDialog({ status: 'hidden' })
    }

    React.useEffect(() => {
        const processOauthCallback = async (domainId: number, _oauthState: string) => {
            setPrimaryActionLoading(true)
            setOauthDialog({ status: 'finalizing' })

            const res = await svc.completeIntegrationSetup(domainId, DomainIntegrationProviders.SHOPIFY, qs, {
                cancellationKey: 'shopify-integration',
            })
            setPrimaryActionLoading(false)

            if (!res.ok || !res.data) {
                setOauthDialog({ status: 'failed' })
                return
            }

            setOauthDialog({ status: 'completed' })
        }

        if (mode === 'add' && domain?.id && qs.provider === DomainIntegrationProviders.SHOPIFY && qs.state) {
            processOauthCallback(domain.id, qs.state.toString())
        }
    }, [mode, domain?.id, qs.state, qs.provider])

    const createIntegration = () => {
        if (loading || mode !== 'add') return
        setShopName('')
        setSetupDialogOpen(true)
    }

    const confirmCreateIntegration = async () => {
        const shop = shopName.trim()
        if (!shop) return

        setConnectLoading(true)

        const redirectUrlRes = await svc.fetchIntegrationRedirectUrl('shopify', domain!.id, {
            shop: `${shop}.myshopify.com`,
        })

        if (!redirectUrlRes.ok) {
            setConnectLoading(false)
            const err = redirectUrlRes.error as any
            handleResponseErrorMessage(err)
            return
        }

        const { authUrl } = redirectUrlRes.data!
        window.location.href = authUrl
    }

    const cancelCreateIntegration = () => {
        setSetupDialogOpen(false)
        setPrimaryActionLoading(false)
    }

    const removeIntegration = () => {
        if (loading || !integration) return
        setRemoveDialogOpen(true)
    }

    const confirmRemoveIntegration = async () => {
        if (!integration || !domain) return
        setRemoveActionLoading(true)

        const { ok } = await svc.removeIntegration(domain.id, integration.id)

        setRemoveActionLoading(false)

        if (ok) {
            setRemoveDialogOpen(false)
            setPrimaryActionLoading(false)
            simpleNotification('success', 'Integration successfully removed.')
            dispatch({ type: 'rem-integration', integration })
        }
    }

    const cancelRemoveIntegration = () => {
        setRemoveDialogOpen(false)
        setPrimaryActionLoading(false)
    }

    return (
        <>
            <PlatformIntegrationCard
                loading={loading}
                className={props.className}
                provider={DomainIntegrationProviders.SHOPIFY}
                logo={shopifyLogo}
                mode={mode}
                isActive={integration?.is_active}
                actions={{
                    primary: {
                        loading: primaryActionLoading,
                        onClick: () => {
                            if (!integration) {
                                createIntegration()
                            } else {
                                removeIntegration()
                            }
                        },
                    },
                }}
            >
                {integration && (
                    <Descriptions layout="horizontal" column={1}>
                        <Descriptions.Item label="Shop">
                            {getShopifyDomainName(integration.data.shop)}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </PlatformIntegrationCard>

            {/* OAuth completion modal — finalizing / completed / failed states */}
            <PlatformIntegrationModal
                className="shopify-oauth-dialog"
                open={oauthDialog.status !== 'hidden'}
                providerLogo={shopifyLogo}
                title={
                    oauthDialog.status === 'finalizing'
                        ? 'Integration Finalizing'
                        : oauthDialog.status === 'completed'
                        ? 'Integration Completed'
                        : 'Integration Failed'
                }
                okText={oauthDialog.status === 'finalizing' ? undefined : 'Close'}
                okButtonProps={{ disabled: oauthDialog.status === 'finalizing' }}
                cancelButtonProps={{ style: { display: 'none' } }}
                onOk={() => {
                    if (oauthDialog.status === 'completed') {
                        simpleNotification('success', 'Integration successfully completed.')
                    }
                    closeOauthDialog()
                }}
                onCancel={closeOauthDialog}
            >
                {oauthDialog.status === 'finalizing' && (
                    <Result icon={<Loading3QuartersOutlined spin={true} />} title={false} />
                )}
                {oauthDialog.status === 'completed' && (
                    <Result
                        status="success"
                        title={false}
                        subTitle={
                            <>
                                <p>
                                    The Platform Shopify app has been successfully installed on your store. Within the
                                    next 15 minutes visitors to your store will begin to be prompted to opt-in to web
                                    push notifications.
                                </p>
                                <p className="details">
                                    Your Shopify product catalog has been automatically imported to the platform and you
                                    may set up Abandonment Notifications via the Journeys section of the platform.
                                </p>
                            </>
                        }
                    />
                )}
                {oauthDialog.status === 'failed' && (
                    <Result
                        status="warning"
                        title={false}
                        subTitle="An error occurred during your integration and we were unable to complete the setup."
                    />
                )}
            </PlatformIntegrationModal>

            {/* Setup modal — collect shop address before redirecting to Shopify OAuth */}
            <PlatformIntegrationModal
                className="shopify-setup-oauth-dialog"
                open={setupDialogOpen}
                providerLogo={shopifyLogo}
                title="Integration Setup"
                okText="Connect"
                okButtonProps={{ loading: connectLoading, disabled: !shopName.trim() }}
                onOk={confirmCreateIntegration}
                onCancel={cancelCreateIntegration}
            >
                <div>
                    Shop address
                    <Input
                        autoFocus={true}
                        value={shopName}
                        onChange={(ev) => setShopName(ev.target.value)}
                        onPressEnter={confirmCreateIntegration}
                        addonAfter={<span>.myshopify.com</span>}
                    />
                </div>
            </PlatformIntegrationModal>

            {/* Remove integration confirmation modal */}
            <PlatformIntegrationModal
                className="shopify-remove-integration-dialog"
                open={removeDialogOpen}
                providerLogo={shopifyLogo}
                title="Remove Integration"
                okText="Remove"
                okButtonProps={{ type: 'remove', loading: removeActionLoading }}
                onOk={confirmRemoveIntegration}
                onCancel={cancelRemoveIntegration}
            >
                {integration && (
                    <>
                        <p>
                            Performing this step will remove the{' '}
                            <b>{getShopifyDomainName(integration.data.shop)} </b>
                            integration and will stop syncing products from your store to the Pushly platform.
                        </p>
                        <p>
                            In order to completely disable the Pushly integration you will also need to navigate to your
                            Shopify Admin and remove the Pushly app.
                        </p>
                    </>
                )}
            </PlatformIntegrationModal>
        </>
    )
}

export default ShopifyIntegration
